import { useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import type { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';

import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { SearchContext } from 'bundles/search-common/providers/SearchContext';
import type {
  SearchConfig,
  SearchQuery,
  SearchQueryVariables,
  SearchResults,
  SearchStates,
} from 'bundles/search-common/providers/searchTypes';
import SEARCH_QUERY from 'bundles/search-common/queries/searchQuery.graphql';
import {
  getProviderFetchMoreUpdateQuery,
  getSearchRequestsFromSearchStates,
  mergeStateWithResults,
} from 'bundles/search-common/utils/providerUtils';

export type Return = {
  isLoading: boolean;
  error?: ApolloError;
  results?: SearchResults;
  loadMore?: (pageNumber: number) => void;
};

/**
 * useSearchProviderQuery is a hook that handles search provider requests.
 * It takes in searchStates and searchConfigs as parameters and returns an object with loading, error, and results.
 * This hook is specific for SearchProvider due to its active filters logic that makes additional requests to populate
 * the search filters options regardless of user selections. After making the request, it's necessary to combine the results into the main
 * index to incorporate the full filter options.
 *
 * @param {SearchStates} searchStates - The search states to be used, default is an empty object.
 * @param {SearchConfig[]} searchConfigs - The initial search configs required just to add back facetFilters (enterpriseIndexConfig).
 * @returns {Return} An object containing isLoading, error, and results.
 *
 * @example
 * useSearchProviderQuery(searchStates, searchConfigs);
 *
 * @note This function should only be used for SearchProvider component.
 */
function useSearchProviderQuery(
  searchStates: SearchStates = {},
  searchConfigs: SearchConfig[],
  ssr: boolean,
  fetchPolicy?: WatchQueryFetchPolicy,
  disableBatching = false
): Return {
  const searchContext = useContext(SearchContext);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // this will set true only if the component is mounted on client side
    // it will be false during SSR and rehydration
    setIsClient(true);
  }, []);

  if (searchContext === undefined) {
    throw new Error('useSearchProviderQuery must be used within a SearchProvider');
  }
  const searchStatesArray = useMemo(() => Object.values(searchStates), [searchStates]);

  const mainSearchIndexPosition = searchStatesArray.findIndex((searchState) => searchState.id === SEARCH_MAIN_INDEX_ID);

  const searchRequests = useMemo(() => getSearchRequestsFromSearchStates(searchStatesArray), [searchStatesArray]);

  // Debug logging for Apollo MockedProvider variable matching issues
  // This helps debug when tests fail due to mock variable mismatches
  // To enable: set DEBUG_APOLLO_MOCKS=true in your test environment
  // eslint-disable-next-line no-process-env
  if (process.env.NODE_ENV === 'test' && process.env.DEBUG_APOLLO_MOCKS) {
    // eslint-disable-next-line no-console
    console.log('Actual GraphQL Request Variables:', JSON.stringify({ requests: searchRequests }, null, 2));
  }

  const { loading, error, data, fetchMore } = useQuery<SearchQuery, SearchQueryVariables>(SEARCH_QUERY, {
    variables: { requests: searchRequests },
    context: { clientName: 'gatewayGql', disableBatching },
    notifyOnNetworkStatusChange: isClient, // needed for fetchMore, only needed in client anyways
    errorPolicy: 'all', // needed so we still render products even if product card even if some are missing from product card application
    ssr,
    fetchPolicy,
  });

  const loadNextPage = (pageNumber: number) => {
    // increment page variable for request
    const indexToLoadMoreOn = mainSearchIndexPosition;
    const updatedSearchParams = searchRequests.map((searchRequest, i) => {
      if (i === indexToLoadMoreOn) {
        const modifiedRequest = { ...searchRequest };
        modifiedRequest.cursor = pageNumber.toString();
        return modifiedRequest;
      } else {
        return searchRequest;
      }
    });
    // make request and combine results
    fetchMore({
      variables: { requests: updatedSearchParams },
      updateQuery: getProviderFetchMoreUpdateQuery(indexToLoadMoreOn),
    });
  };

  const results = useMemo(
    () => mergeStateWithResults(data, searchStatesArray, searchConfigs),
    [data, searchStatesArray, searchConfigs]
  );

  return {
    isLoading: loading,
    error,
    results,
    loadMore: loadNextPage,
  };
}

export default useSearchProviderQuery;
